import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import * as momentAlias from 'moment';
import { validateMaxEndDate, validateMinStartDate } from './min-and-max-date.validator';
const moment = momentAlias;

@Component({
  selector: 'ihm-unified-start-end-date-picker',
  templateUrl: './ihm-unified-start-end-date-picker.component.html',
  styleUrls: ['./ihm-unified-start-end-date-picker.component.scss']
})
export class IhmUnifiedStartEndDatePickerComponent implements OnInit, OnChanges {
  // Inputs
  @Input() readonly startDate: string | Date;

  @Input() readonly endDate: string | Date;

  @Input() readonly minStartDate: string | Date;
  @Input() readonly maxEndDate: string | Date;

  @Input() readonly minEndDateOffsetDays: number;

  @Input() readonly isStartDateDisabled: boolean;
  @Input() readonly isEndDateDisabled: boolean;
  @Input() readonly arePastDatesEnabled: boolean;

  // Outputs
  @Output() readonly datesUpdated: EventEmitter<{
    startDate: string;
    endDate: string;
  }> = new EventEmitter<{
    startDate: string;
    endDate: string;
  }>();

  // Public variables
  formattedStartDate: Date;
  formattedEndDate: Date;

  startDateField: FormControl;
  endDateField: FormControl;
  getMinDateFn: Function = this.getMinDate.bind(this);

  // Private variables
  private currentStartDate: Date;
  private readonly SHORT_DATE_FORMAT: string = 'YYYY-MM-DD';

  constructor() {}

  ngOnInit(): void {
    // Initialize form controls.
    this.startDateField = new FormControl(this.startDate, [validateMinStartDate(this.minStartDate)]);
    this.endDateField = new FormControl(this.endDate, [validateMaxEndDate(this.maxEndDate)]);

    this.currentStartDate = moment(this.startDateField.value).toDate();
    this.setFormattedDates();

    this.applyMinAndMaxValidations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('minStartDate' in changes || 'maxEndDate' in changes) {
      this.applyMinAndMaxValidations();
    }
  }

  getMinEndDate(): Date {
    if (!this.startDateField) {
      return null;
    }

    const startDate: momentAlias.Moment = this.startDateField.value ? moment(this.startDateField.value) : this.getTodaysDate();

    if (this.minEndDateOffsetDays > 0) {
      const offsetDaysFromToday = this.getTodaysDate().add(this.minEndDateOffsetDays, 'days');
      return startDate > offsetDaysFromToday ? startDate.toDate() : offsetDaysFromToday.toDate();
    } else {
      return startDate.toDate();
    }
  }

  onUpdateButtonClick(): void {
    this.datesUpdated.emit({ startDate: this.startDateField.value, endDate: this.endDateField.value });
    this.formattedStartDate = moment(this.startDateField.value).toDate();
    this.formattedEndDate = moment(this.endDateField.value).toDate();
  }

  onUpdateStartDate(newStartDate: Date): void {
    const currentStartDate: momentAlias.Moment = moment(this.currentStartDate);
    const currentEndDate: momentAlias.Moment = moment(this.endDateField.value);

    if (moment(newStartDate).isAfter(currentEndDate)) {
      // if the user changed the start date to something after the end date, modify the end date keeping the difference in days
      const diffInDays = currentEndDate.diff(currentStartDate, 'days');
      const newEndDate = moment(newStartDate).add(diffInDays, 'days').format(this.SHORT_DATE_FORMAT);
      this.endDateField.setValue(newEndDate);
    }

    this.currentStartDate = newStartDate;
  }

  private getTodaysDate(): momentAlias.Moment {
    return moment().startOf('day');
  }

  private setFormattedDates(): void {
    this.formattedStartDate = !isNaN(Date.parse(this.startDateField.value))
      ? moment(this.startDateField.value).toDate()
      : this.getTodaysDate().toDate();

    this.formattedEndDate = !isNaN(Date.parse(this.endDateField.value)) ? moment(this.endDateField.value).toDate() : this.getMinEndDate();
  }

  private applyMinAndMaxValidations(): void {
    this.applyMinStartDateValidations();
    this.applyMaxEndDateValidations();
  }

  private applyMinStartDateValidations(): void {
    if (this.startDateField) {
      this.startDateField.setValidators(validateMinStartDate(this.minStartDate));
      this.startDateField.markAsTouched();
      this.startDateField.updateValueAndValidity();
    }
  }

  private applyMaxEndDateValidations(): void {
    if (this.endDateField) {
      this.endDateField.setValidators(validateMaxEndDate(this.maxEndDate));
      this.endDateField.markAsTouched();
      this.endDateField.updateValueAndValidity();
    }
  }

  private getMinDate(modalDate: Date): boolean {
    const fieldDate: momentAlias.Moment = moment(modalDate || new Date());
    if (this.startDateField) {
      const startDate: momentAlias.Moment = moment(this.startDateField.value);
      if (fieldDate.diff(startDate, 'days') === 0) {
        return true;
      }
    }
    return this.arePastDatesEnabled ? true : fieldDate.isAfter(this.getTodaysDate());
  }
}
